/**
 * Distributes a total value across a set of target indices, either by
 * weights (IA ON) or equally (IA OFF).
 *
 * All shares are rounded to 2 decimal places and the last target index
 * receives the remainder so the sum always equals `total` exactly.
 *
 * @param total          The amount to distribute
 * @param targetIndices  Indices that should receive a share (must be non-empty)
 * @param weights        Optional full-length weight array indexed by day position.
 *                       When provided and the sum of weights for targetIndices > 0,
 *                       weighted distribution is used. Otherwise equal distribution.
 * @returns A Map<number, number> where key = day index, value = distributed goal
 */
export function distributeGoalValues(
  total: number,
  targetIndices: number[],
  weights?: number[]
): Map<number, number> {
  const result = new Map<number, number>()

  if (targetIndices.length === 0) return result

  const lastIdx = targetIndices[targetIndices.length - 1]

  // Determine if we should use weighted or equal distribution
  const totalWeight = weights
    ? targetIndices.reduce((sum, i) => sum + (weights[i] || 0), 0)
    : 0

  const useWeighted = !!weights && totalWeight > 0

  let distributed = 0

  for (const idx of targetIndices) {
    if (idx === lastIdx) {
      // Last target gets the remainder to ensure exact sum
      const remainder = parseFloat((total - distributed).toFixed(2))
      result.set(idx, remainder)
    } else {
      const share = useWeighted
        ? ((weights![idx] || 0) / totalWeight) * total
        : total / targetIndices.length

      const rounded = parseFloat(share.toFixed(2))
      distributed += rounded
      result.set(idx, rounded)
    }
  }

  return result
}
